import { ChevronUp, ChevronDown, X, Plus } from 'lucide-react';
import { PivotAxis } from '@/lib/pivot';

/**
 * Ordered multi-select for pivot axis keys (rows or columns). Selected axes show
 * as reorderable chips; an "add" dropdown offers the remaining axes. Keys already
 * used on the *other* axis are excluded so a key never lands on both.
 */
export function DimensionPicker({
  label, axes, value, exclude, onChange,
}: {
  label: string;
  axes: PivotAxis[];
  value: string[];
  exclude: string[];
  onChange: (next: string[]) => void;
}) {
  const byKey = new Map(axes.map((a) => [a.key, a]));
  const available = axes.filter((a) => !value.includes(a.key) && !exclude.includes(a.key));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = value.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div>
      <span className="block text-xs font-semibold text-ink-700 mb-1">{label}</span>
      <div className="space-y-1.5">
        {value.length === 0 && <p className="text-[11px] text-muted-foreground italic">—</p>}
        {value.map((k, i) => (
          <div key={k} className="flex items-center gap-1 rounded-lg border border-border bg-white px-2 py-1">
            <span className="flex-1 text-sm text-ink-800 truncate">
              {byKey.get(k)?.label ?? k}
              {!byKey.get(k)?.synthetic && <span className="ml-1 text-[10px] text-muted-foreground num">{k}</span>}
            </span>
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-ink-400 hover:text-ink-700 disabled:opacity-30 p-0.5"><ChevronUp className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === value.length - 1} className="text-ink-400 hover:text-ink-700 disabled:opacity-30 p-0.5"><ChevronDown className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => onChange(value.filter((x) => x !== k))} className="text-ink-400 hover:text-warn-600 p-0.5"><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        {available.length > 0 && (
          <div className="flex items-center gap-1">
            <Plus className="h-3.5 w-3.5 text-emerald-600" />
            <select
              value=""
              onChange={(e) => { if (e.target.value) onChange([...value, e.target.value]); }}
              className="flex-1 rounded-lg border border-dashed border-border bg-white px-2 py-1 text-sm text-ink-600"
            >
              <option value="">{`add field…`}</option>
              {available.map((a) => <option key={a.key} value={a.key}>{a.label}{!a.synthetic ? ` (${a.key})` : ''}</option>)}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
